import { splitFrontMatter } from '@westhetics/legal'
import type { DocumentType } from './api'

/**
 * As verificações que a API faz ao publicar, além do lint do dialeto (docs/api/documentos.md,
 * `POST /documents/templates/:id/versions`; RN-DOC-12 e RN-DOC-13). A tela repete aqui para dizer
 * a causa e o caminho antes do clique; quem decide é sempre a API.
 */

/** Limite do Markdown de uma versão, em bytes UTF-8 (P-DOC-23). */
const MAX_MARKDOWN_BYTES = 204_800

/** A data da publicação para a API: hoje no fuso de São Paulo, `aaaa-mm-dd`. */
export function todayInSaoPaulo(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(now)
}

/** "2026-10-03" → "03/10/2026", sem passar por fuso. */
function longDate(isoDate: string) {
  const [year, month, day] = isoDate.split('-')
  return `${day}/${month}/${year}`
}

/** As datas dos blocos `:::update{date="…"}` do texto, na ordem em que aparecem. */
export function updateBlockDates(markdown: string): string[] {
  const dates: string[] = []
  const block = /^[ \t]{0,3}:{3,}update\{[^}\n]*\bdate=["']?(\d{4}-\d{2}-\d{2})["']?[^}\n]*\}/gm
  for (const match of markdown.matchAll(block)) if (match[1]) dates.push(match[1])
  return dates
}

export function publishIssues(input: {
  draft: string
  templateType: DocumentType
  /** O número que a versão vai receber. */
  number: number
  today: string
}): string[] {
  const issues: string[] = []
  const frontMatter = splitFrontMatter(input.draft)
  const type = typeof frontMatter.data.type === 'string' ? frontMatter.data.type.trim() : ''
  const title = typeof frontMatter.data.title === 'string' ? frontMatter.data.title.trim() : ''
  // YAML quebrado já aparece como erro de dialeto.
  if (!frontMatter.isInvalid && (!type || !title)) {
    issues.push('O cabeçalho do documento (front matter) precisa de type e title.')
  }
  if (type && type !== input.templateType) {
    issues.push(
      `O cabeçalho do documento diz type: ${type}, mas este documento é ${input.templateType}. Troque a linha type no editor.`,
    )
  }
  if (new TextEncoder().encode(input.draft).length > MAX_MARKDOWN_BYTES) {
    issues.push('O Markdown passa de 200 KB. Divida o documento ou reduza o texto.')
  }
  if (input.number >= 2) {
    const dates = updateBlockDates(input.draft)
    if (!dates.includes(input.today)) {
      const last = dates.at(-1)
      issues.push(
        last
          ? `O bloco "Cláusula atualizada" está com a data de ${longDate(last)}. A publicação é de hoje: troque para ${longDate(input.today)} no editor.`
          : 'Acrescente no editor um bloco "Cláusula atualizada" com a data de hoje e o motivo da mudança.',
      )
    }
  }
  return issues
}
